import React from 'react';
import { Calendar, MapPin, Package, User, Clock, AlertTriangle } from 'lucide-react';

interface AssetDetailsProps {
  asset: {
    id: string;
    equipmentInventory: string;              // Equipment category ID
    equipmentInventoryName?: string;         // Equipment category name (for display)
    equipmentSubcategory?: string;           // Subcategory ID
    equipmentSubcategoryName?: string;       // Subcategory name (for display)
    location: string;
    floor: string;
    capacity?: string;
    type?: string;
    cylinderNumber?: string;
    panelNo?: string;
    loopNo?: string;
    deviceNo?: string;
    loopType?: string;
    equipmentManufactureDate?: string;
    hptTestDate?: string;
    lastRefillDate?: string;
    description?: string;
    qty?: string | number;
    premisesName?: string;
    vendorName?: string;
    mobileNumber?: string;
    vendorEmail?: string;
    lastInspectionDate?: string;
    nextInspectionDate?: string;
    inspectionStatus?: 'due' | 'overdue' | 'completed' | 'upcoming';
  };
  onStartInspection: () => void;
}

const AssetDetails: React.FC<AssetDetailsProps> = ({ asset, onStartInspection }) => {

  const isInspectionDisabled = React.useMemo(() => {
    if (!asset.nextInspectionDate) return false;
    
    const nextInspectionDate = new Date(asset.nextInspectionDate);
    
    if (isNaN(nextInspectionDate.getTime())) return false;
    
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    nextInspectionDate.setHours(0, 0, 0, 0);
    
    return currentDate < nextInspectionDate;
  }, [asset.nextInspectionDate]);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'overdue':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'due':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'upcoming':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'overdue':
        return <AlertTriangle size={16} className="text-red-500" />;
      case 'due':
        return <Clock size={16} className="text-orange-500" />;
      case 'completed':
        return <Calendar size={16} className="text-green-500" />;
      default:
        return <Calendar size={16} className="text-blue-500" />;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getAssetTypeSpecificFields = () => {
    const fields = [];
    
    // CO2 Fire Extinguisher specific fields
    if (asset.capacity) {
      fields.push({ label: 'Capacity', value: asset.capacity, icon: Package });
    }
    if (asset.cylinderNumber) {
      fields.push({ label: 'Cylinder Number', value: asset.cylinderNumber, icon: Package });
    }
    if (asset.lastRefillDate) {
      fields.push({ label: 'Last Refill Date', value: formatDate(asset.lastRefillDate), icon: Calendar });
    }
    if (asset.hptTestDate) {
      fields.push({ label: 'HPT Test Date', value: formatDate(asset.hptTestDate), icon: Calendar });
    }

    // Fire Panel specific fields
    if (asset.panelNo) {
      fields.push({ label: 'Panel Number', value: asset.panelNo, icon: Package });
    }
    if (asset.loopNo) {
      fields.push({ label: 'Loop Number', value: asset.loopNo, icon: Package });
    }
    if (asset.deviceNo) {
      fields.push({ label: 'Device Number', value: asset.deviceNo, icon: Package });
    }
    if (asset.loopType) {
      fields.push({ label: 'Loop Type', value: asset.loopType, icon: Package });
    }

    return fields;
  };

  const assetSpecificFields = getAssetTypeSpecificFields();

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-0">
        <div className="flex justify-between items-start mb-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {asset.equipmentInventoryName || asset.equipmentInventory}
            </h2>
            <p className="text-gray-600 mt-1">
              {asset.type && <span className="font-medium">Type: {asset.type}</span>}
              {asset.type && asset.cylinderNumber && <span className="mx-2">•</span>}
              {asset.cylinderNumber && <span>Cylinder: {asset.cylinderNumber}</span>}
            </p>
          </div>
          {asset.inspectionStatus && (
            <div className={`px-3 py-1 rounded-full border text-sm font-medium flex items-center gap-1 ${getStatusColor(asset.inspectionStatus)}`}>
              {getStatusIcon(asset.inspectionStatus)}
              <span className="capitalize">{asset.inspectionStatus}</span>
            </div>
          )}
        </div>
        
        {/* {asset.equipmentSubcategoryName && (
          <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            {asset.equipmentSubcategoryName}
          </div>
        )} */}
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Basic Information</h3>
          
          <div className="flex items-center gap-3">
            <MapPin size={18} className="text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="font-medium">{asset.location}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Package size={18} className="text-gray-500" />
            <div>
              <p className="text-sm text-gray-500">Floor</p>
              <p className="font-medium">{asset.floor}</p>
            </div>
          </div>

          {asset.type && (
            <div className="flex items-center gap-3">
              <Package size={18} className="text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="font-medium">{asset.type}</p>
              </div>
            </div>
          )}

          {asset.qty && (
            <div className="flex items-center gap-3">
              <Package size={18} className="text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Quantity</p>
                <p className="font-medium">{asset.qty}</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Inspection Status</h3>
          
          {asset.lastInspectionDate && (
            <div className="flex items-center gap-3">
              <Calendar size={18} className="text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Last Inspection</p>
                <p className="font-medium">{formatDate(asset.lastInspectionDate)}</p>
              </div>
            </div>
          )}

          {asset.nextInspectionDate && (
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Next Inspection Due</p>
                <p className="font-medium">{formatDate(asset.nextInspectionDate)}</p>
              </div>
            </div>
          )}

          {asset.equipmentManufactureDate && (
            <div className="flex items-center gap-3">
              <Calendar size={18} className="text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Manufacture Date</p>
                <p className="font-medium">{formatDate(asset.equipmentManufactureDate)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Asset Type Specific Fields */}
      {assetSpecificFields.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Asset Specific Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {assetSpecificFields.map((field, index) => (
              <div key={index} className="flex items-center gap-3">
                <field.icon size={18} className="text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">{field.label}</p>
                  <p className="font-medium">{field.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {asset.description && asset.description !== 'N/A' && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Description</h3>
          <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{asset.description}</p>
        </div>
      )}

      {/* Vendor Information */}
      {(asset.vendorName || asset.vendorEmail || asset.mobileNumber) && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Vendor Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {asset.vendorName && (
              <div className="flex items-center gap-3">
                <User size={18} className="text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Vendor Name</p>
                  <p className="font-medium">{asset.vendorName}</p>
                </div>
              </div>
            )}
            
            {asset.vendorEmail && (
              <div className="flex items-center gap-3">
                <User size={18} className="text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Vendor Email</p>
                  <p className="font-medium">{asset.vendorEmail}</p>
                </div>
              </div>
            )}
            
            {asset.mobileNumber && (
              <div className="flex items-center gap-3">
                <User size={18} className="text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Contact Number</p>
                  <p className="font-medium">{asset.mobileNumber}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Button */}
      <div className="border-t border-gray-200 pt-4 space-y-3">
        <button
          onClick={isInspectionDisabled ? undefined : onStartInspection}
          disabled={isInspectionDisabled}
          className={`w-full md:w-auto px-8 py-3 rounded-lg transition-colors font-medium flex items-center justify-center gap-2 ${
            isInspectionDisabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
              : 'bg-[#00A8A5] text-white hover:bg-[#008A87] cursor-pointer'
          }`}
        >
          <Calendar size={20} />
          Start Inspection
        </button>
        
        {isInspectionDisabled && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Inspection not yet due</p>
              <p className="text-amber-700 mt-1">
                The next inspection is scheduled for {formatDate(asset.nextInspectionDate)}. 
                Please wait until this date to start the inspection.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetDetails;


